// Candidates tab helpers (SPT-2164 phase 2): labels for the derived enrollment state and the
// engine's hold / exit reasons, the history timeline copy, and the client-side CSV export.
import { pluralize } from './cadence.utils'
import { HOLDOUT_REASON } from './holdout.utils'
import { JOB_ALERT_CHANNEL_LABELS } from './job-alerts.utils'
import { subjectLine } from './liquid-text.utils'

const sentenceCase = (value) => {
	const text = String(value || '')
		.replace(/[_-]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
	return text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : ''
}

// Server enum CadenceEnrollmentState. PAUSED is a legacy (graph v1) state and is not a filter option.
export const ENROLLMENT_STATES = ['LIVE', 'HELD', 'COMPLETED', 'EXITED']
export const ENROLLMENT_STATE_LABELS = { LIVE: 'Live', HELD: 'Held', COMPLETED: 'Completed', EXITED: 'Exited', PAUSED: 'Paused' }
export const enrollmentStateLabel = (state) => ENROLLMENT_STATE_LABELS[state] || sentenceCase(state) || 'Unknown'

export const enrollmentStatePillStyle = (state) =>
	({
		LIVE: { color: '#309102', background: '#ECFDF3' },
		HELD: { color: '#B26A00', background: '#FFF6DE' },
		EXITED: { color: '#D92D20', background: '#FFF4F3' },
	}[state] || { color: '#5E5E60', background: '#F5F5F5' })

// CadenceEnrollment::EXIT_REASONS
const EXIT_REASON_LABELS = {
	goal_met: 'Goal met',
	unsubscribed: 'Unsubscribed',
	completed: 'Completed',
	cadence_archived: 'Workflow archived',
	no_path_expired: 'No matching path',
	recheck_no_match: 'Stopped matching',
	no_matching_branch: 'No matching branch',
	undeliverable: 'Undeliverable email',
}
// AdvanceEnrollmentService holds and DeliverStepService skips
const HELD_REASON_LABELS = {
	outside_audience: 'Outside the audience',
	no_path: 'No matching path',
	max_steps_exceeded: 'Too many steps',
	delivery_failed: 'Delivery failed',
	cadence_not_sendable: 'Workflow not live',
	no_email: 'No email address',
	email_disabled: 'Email switched off',
	unsubscribed: 'Unsubscribed',
	restricted_email: 'Email not on the allowed list',
	template_unavailable: 'Template unavailable',
	suppressed: 'Suppressed',
	awaiting_graph_backfill: 'Awaiting upgrade',
	already_sent: 'Already sent',
	delivery_unknown: 'Delivery unconfirmed',
	lease_expired_before_handoff: 'Delivery timed out',
	lease_expired_after_handoff: 'Delivery unconfirmed',
}
export const exitReasonLabel = (reason) => (reason ? EXIT_REASON_LABELS[reason] || sentenceCase(reason) : '')
export const heldReasonLabel = (reason) => (reason ? HELD_REASON_LABELS[reason] || sentenceCase(reason) : '')

// The status column: the state plus why, e.g. "Held · No matching path", "Exited · Unsubscribed".
export const enrollmentStatusDetail = (row) => {
	if (row?.state === 'HELD') return heldReasonLabel(row.heldReason)
	if (row?.state === 'EXITED') return exitReasonLabel(row.exitReason)
	return ''
}

export const candidateName = (worker) => {
	const name = String(worker?.fullName || [worker?.firstName, worker?.lastName].filter(Boolean).join(' ')).trim()
	return name || worker?.email || 'Unknown candidate'
}

// The step an enrollment sits on, without the graph: the API carries the template for message steps.
export const enrollmentStepLabel = (step) => {
	if (!step) return 'Finished'
	switch (step.kind) {
		case 'email':
			return step.cadenceEmailTemplate?.name || 'Email'
		case 'sms':
			return step.cadenceEmailTemplate?.name || 'Text'
		case 'push':
			return step.cadenceEmailTemplate?.name || 'Push'
		case 'delay':
			return `Wait ${pluralize(step.delayAmount, step.delayUnit || 'day')}`
		case 'recheck':
			return 'Re-check'
		case 'split':
			return 'Split'
		case 'end':
			return 'End'
		default:
			return sentenceCase(step.kind) || 'Step'
	}
}

const pad = (value) => String(value).padStart(2, '0')
// "Sep 22, 2026 · 14:05" in the viewer's local time; fixed format so tests do not depend on the locale.
export const formatCadenceDateTime = (value) => {
	if (!value) return null
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return null
	const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][date.getMonth()]
	return `${month} ${date.getDate()}, ${date.getFullYear()} · ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const parseDetail = (value) => {
	if (value && typeof value === 'object') return value
	if (typeof value !== 'string') return {}
	try {
		const parsed = JSON.parse(value)
		return parsed && typeof parsed === 'object' ? parsed : {}
	} catch (_error) {
		return {}
	}
}

// Why a job alert failed and what happens to its jobs. `handoff_started` false means nothing reached the candidate,
// so the jobs go out with the next alert; true means it may have, so they are not sent again on that channel.
const jobAlertFailureDetail = (meta) => {
	// The API passes only the HTTP status of the raw error (`error_status`); `error` itself is kept out of the drawer.
	const status = meta.error_status ? String(meta.error_status) : String(meta.error || '').match(/status (\d{3})/)?.[1]
	const attempted = Boolean(meta.error) || typeof meta.handoff_started === 'boolean'
	const cause =
		status === '503' ? 'The service was unavailable' : status ? `The service answered with error ${status}` : attempted ? 'The send did not go through' : ''
	if (!cause) return ''
	if (meta.handoff_started === false) return `${cause}. Nothing was sent; these jobs go out with the next alert.`
	if (meta.handoff_started === true) return `${cause}. It may have reached the candidate, so these jobs are not sent again on this channel.`
	return `${cause}.`
}

// SPT-2316: a job alert delivery is logged as a sent/failed event whose detail (or its `meta`) carries
// `kind: "job_alert"`, the `channel` and the `job_ids` it announced. Returns null for any other event.
export const describeJobAlertEvent = (event) => {
	const detail = parseDetail(event?.detail)
	const meta = { ...detail, ...parseDetail(detail.meta) }
	if (meta.kind !== 'job_alert' || !['sent', 'failed'].includes(event?.eventType)) return null
	const channel = JOB_ALERT_CHANNEL_LABELS[meta.channel] || sentenceCase(meta.channel) || 'Email'
	if (event.eventType === 'failed') return { title: `Job alert failed · ${channel}`, detail: jobAlertFailureDetail(meta) || heldReasonLabel(event.reason) }
	const jobs = Array.isArray(meta.job_ids) ? meta.job_ids.length : 0
	return { title: `Job alert sent · ${channel}${jobs ? ` (${pluralize(jobs, 'job')})` : ''}`, detail: '' }
}

// Smart communication S1: a candidate in the control group walks the workflow, and each message step is logged as
// `skipped` with reason `holdout` (in `reason` and in the detail/meta, with the step's `channel`). It is not a hold.
const holdoutMeta = (event) => {
	if (event?.eventType !== 'skipped') return null
	const detail = parseDetail(event.detail)
	const meta = { ...detail, ...parseDetail(detail.meta) }
	return (event.reason || meta.reason) === HOLDOUT_REASON ? meta : null
}
export const isHoldoutEvent = (event) => Boolean(holdoutMeta(event))

const describeHoldoutEvent = (meta) => {
	const channel = JOB_ALERT_CHANNEL_LABELS[meta.channel] || sentenceCase(meta.channel) || 'Message'
	return { title: 'Not sent · control group', detail: `${channel} step skipped: this candidate is in the control group.` }
}

const MESSAGE_NOUNS = { email: 'Email', sms: 'Text', push: 'Push' }

// One timeline row per CadenceEvent: what happened, in the admin's words, plus the detail line under it.
export const describeEnrollmentEvent = (event) => {
	const jobAlert = describeJobAlertEvent(event)
	if (jobAlert) return jobAlert
	const holdout = holdoutMeta(event)
	if (holdout) return describeHoldoutEvent(holdout)
	const detail = event?.detail || {}
	const path = event?.path?.name || event?.path?.key || detail.path || detail.to || null
	const template = event?.step?.cadenceEmailTemplate
	// The message noun follows the step's channel: "Text sent", "Push failed"; email when the step is unknown.
	const message = MESSAGE_NOUNS[event?.step?.kind] || 'Email'
	switch (event?.eventType) {
		case 'enrolled':
			return { title: 'Enrolled', detail: '' }
		case 'path_entered':
			return { title: path ? `Entered path “${path}”` : 'Entered a path', detail: '' }
		case 'rechecked':
			return {
				title: detail.matched === false ? 'Re-check: no longer matches' : 'Re-check: still matches',
				detail: path ? `Path “${path}”` : '',
			}
		case 'switched':
			return {
				title: `Switched path ${detail.from || '?'} → ${detail.to || '?'}`,
				detail: detail.switch_timing ? `Timing: ${sentenceCase(detail.switch_timing)}` : '',
			}
		case 'branched':
			return { title: detail.branch ? `Took branch “${detail.branch}”` : 'No branch matched', detail: detail.split ? `At split “${detail.split}”` : '' }
		case 'pending':
			return { title: `${message} being sent`, detail: template?.name || '' }
		case 'unknown':
			return { title: 'Delivery unconfirmed', detail: template?.name || '' }
		case 'failed':
			return { title: `${message} failed`, detail: heldReasonLabel(event.reason) }
		case 'sent':
			return { title: `${message} sent${template?.name ? `: ${template.name}` : ''}`, detail: subjectLine(template?.subject) }
		case 'skipped':
			return { title: `Held: ${heldReasonLabel(event.reason) || 'unknown reason'}`, detail: 'Re-tried on the next run' }
		case 'exited':
			return event?.reason === 'completed'
				? { title: 'Completed', detail: 'Reached the end of the path' }
				: { title: `Exited: ${exitReasonLabel(event?.reason) || 'unknown reason'}`, detail: '' }
		default:
			return { title: sentenceCase(event?.eventType) || 'Event', detail: '' }
	}
}

// ── Backend without an enrollments API (deploy skew, older gem) ─────────────
//
// The portal can be deployed ahead of the Maestro gem, and a tenant may sit on a gem branch that
// never got the enrollments API. Selecting `enrollmentSummary` / `cadenceEnrollments` against such a
// schema is a *validation* failure: GraphQL rejects the whole document, so the response carries no
// data at all — "Field 'enrollmentSummary' doesn't exist on type 'MaestroCadence'", extensions code
// `undefinedField`. Nothing is wrong with the workflow, so the Candidates tab is hidden rather than
// the admin being told their workflow is broken. A new argument on an older schema fails the same way:
// "Field 'planCadenceWithAi' doesn't accept argument 'cadenceId'", code `argumentNotAccepted`.
const UNSUPPORTED_FIELD_PATTERN = /doesn't exist on type|Field '(enrollmentSummary|cadenceEnrollments?|[^']*Enrollment[^']*)' doesn't exist|undefinedField|doesn't accept argument|argumentNotAccepted/i

const mentionsUnsupportedField = (value) => Boolean(value) && UNSUPPORTED_FIELD_PATTERN.test(String(value))

// Accepts an ApolloError (`graphQLErrors` + the joined `message`), a raw `errors` array as the
// Apollo error link sees it, or a plain `{ message }`.
export const isUnsupportedFieldError = (error) => {
	if (!error) return false
	const graphQLErrors = Array.isArray(error) ? error : error.graphQLErrors || error.result?.errors || []
	if (graphQLErrors.some((item) => mentionsUnsupportedField(item?.message) || mentionsUnsupportedField(item?.extensions?.code))) return true
	return mentionsUnsupportedField(error.message)
}

// One verdict per session: once the backend has said it has no enrollments API, every later
// workflow gets the same answer, so the summary query is not re-sent for each one.
let enrollmentsUnsupported = false
export const isEnrollmentsUnsupported = () => enrollmentsUnsupported
export const markEnrollmentsUnsupported = () => {
	enrollmentsUnsupported = true
}
export const resetEnrollmentsSupport = () => {
	enrollmentsUnsupported = false
}

// ── CSV export (client-side, the loaded rows only) ──────────────────────────

export const CSV_EXPORT_LIMIT = 1000

// A zero-width BOM so Excel opens the export as UTF-8 instead of guessing (and mangling) the encoding.
export const CSV_BOM = '﻿'

export const csvCell = (value) => {
	const text = value === null || value === undefined ? '' : String(value)
	const quoted = /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
	// Formula-injection guard: Excel/Sheets can execute a cell that opens with =, +, -, @ as a formula.
	// Applied after quoting so the leading `'` lands inside any quotes already added above.
	if (!/^[=+\-@]/.test(text)) return quoted
	return quoted.startsWith('"') ? `"'${quoted.slice(1)}` : `'${quoted}`
}

export const CSV_COLUMNS = ['Name', 'Email', 'Path', 'Current step', 'Status', 'Reason', 'Enrolled', 'Next run', 'Emails sent']

export const enrollmentsToCsv = (rows) => {
	const lines = (rows || [])
		.slice(0, CSV_EXPORT_LIMIT)
		.map((row) =>
			[
				candidateName(row.worker),
				row.worker?.email,
				row.path?.name || row.path?.key,
				enrollmentStepLabel(row.currentStep),
				enrollmentStateLabel(row.state),
				enrollmentStatusDetail(row),
				row.enrolledAt,
				row.nextRunAt,
				row.sentCount ?? 0,
			]
				.map(csvCell)
				.join(',')
		)
	return `${CSV_BOM}${[CSV_COLUMNS.join(','), ...lines].join('\r\n')}`
}

// `suffix` names what is exported: the candidates (default) or, on the Performance tab, the step table.
export const csvFileName = (cadenceName, suffix = 'candidates') =>
	`${
		String(cadenceName || 'workflow')
			.replace(/[^\w.-]+/g, '-')
			.replace(/^-|-$/g, '') || 'workflow'
	}-${suffix}.csv`

// Moved to a shared helper (View Applicants export uses it too); kept here for existing imports.
export { downloadTextFile } from '~/common/helpers/download.helper'
